import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useContractsExpiring, useContractsExpired, useContractStats } from '../../hooks/useContracts';

interface ContractExpiryAlertsProps {
  showExpired?: boolean;
  maxItems?: number;
}

export default function ContractExpiryAlerts({ showExpired = true, maxItems = 5 }: ContractExpiryAlertsProps) {
  const { t } = useTranslation();
  const { data: expiring30 } = useContractsExpiring(30);
  const { data: expired } = useContractsExpired();
  const { data: stats } = useContractStats();

  const getDaysUntilExpiry = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getUrgencyColor = (days: number) => {
    if (days <= 7) return 'text-red-600 bg-red-50';
    if (days <= 15) return 'text-orange-600 bg-orange-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg bg-red-50 p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
            <p className="text-sm text-red-700">{t('contracts.expired', 'Expirés')}</p>
          </div>
          <div className="rounded-lg bg-orange-50 p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.expiringIn30Days}</p>
            <p className="text-sm text-orange-700">{t('contracts.expiringIn30Days', '< 30 jours')}</p>
          </div>
          <div className="rounded-lg bg-yellow-50 p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.expiringIn60Days}</p>
            <p className="text-sm text-yellow-700">{t('contracts.expiringIn60Days', '30-60 jours')}</p>
          </div>
          <div className="rounded-lg bg-blue-50 p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.expiringIn90Days}</p>
            <p className="text-sm text-blue-700">{t('contracts.expiringIn90Days', '60-90 jours')}</p>
          </div>
        </div>
      )}

      {/* Expired Contracts */}
      {showExpired && expired && expired.items.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('contracts.expiredContracts', 'Contrats expirés')}
            <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
              {expired.total}
            </span>
          </h3>
          <div className="space-y-3">
            {expired.items.slice(0, maxItems).map((contract: any) => (
              <Link
                key={contract.id}
                to={`/contracts/${contract.id}`}
                className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">{contract.title}</p>
                  <p className="text-sm text-gray-500">{contract.code} - {contract.supplier_name || 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-red-600">
                    {t('contracts.expiredOn', 'Expiré le')} {new Date(contract.end_date).toLocaleDateString('fr-FR')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {contract.total_amount?.toLocaleString('fr-FR')} TND
                  </p>
                </div>
              </Link>
            ))}
            {expired.total > maxItems && (
              <Link
                to="/contracts?status=expired"
                className="block text-center text-sm text-blue-600 hover:text-blue-800"
              >
                {t('common.viewAll', 'Voir tout')} ({expired.total})
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Expiring Soon */}
      {expiring30 && expiring30.items.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <svg className="h-5 w-5 text-orange-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {t('contracts.expiringSoon', 'Contrats expirant bientôt')}
            <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
              {expiring30.total}
            </span>
          </h3>
          <div className="space-y-3">
            {expiring30.items.slice(0, maxItems).map((contract: any) => {
              const daysLeft = getDaysUntilExpiry(contract.end_date);
              const urgencyClass = getUrgencyColor(daysLeft);
              return (
                <Link
                  key={contract.id}
                  to={`/contracts/${contract.id}`}
                  className={`flex items-center justify-between p-3 rounded-lg hover:opacity-80 transition-opacity ${urgencyClass}`}
                >
                  <div>
                    <p className="font-medium text-gray-900">{contract.title}</p>
                    <p className="text-sm text-gray-500">{contract.code} - {contract.supplier_name || 'N/A'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      {daysLeft <= 0 ? (
                        <span className="text-red-600">{t('contracts.expired', 'Expiré')}</span>
                      ) : daysLeft === 1 ? (
                        <span className="text-red-600">{t('contracts.expiresIn1Day', 'Expire demain')}</span>
                      ) : (
                        <span>{t('contracts.expiresInDays', 'Expire dans {{days}} jours', { days: daysLeft })}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(contract.end_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </Link>
              );
            })}
            {expiring30.total > maxItems && (
              <Link
                to="/contracts?expiring=30"
                className="block text-center text-sm text-blue-600 hover:text-blue-800"
              >
                {t('common.viewAll', 'Voir tout')} ({expiring30.total})
              </Link>
            )}
          </div>
        </div>
      )}

      {/* No Alerts */}
      {(!expiring30 || expiring30.items.length === 0) && (!expired || expired.items.length === 0) && (
        <div className="rounded-lg bg-green-50 p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-green-800">
            {t('contracts.noExpiringContracts', 'Aucun contrat n\'expire prochainement')}
          </h3>
          <p className="mt-1 text-sm text-green-600">
            {t('contracts.allContractsValid', 'Tous vos contrats sont valides pour les 30 prochains jours.')}
          </p>
        </div>
      )}
    </div>
  );
}
